// ============================================================================
// TRIMA MOCKS — Stand-in for BE responses until real endpoints are ready
// ============================================================================
// To remove mocks: delete this folder and flip TRIMA_USE_MOCKS to false (or
// remove the mock branches from useAPITrima.tsx).
//
// Mock content is inspired by the Trima IBC PDF (24 questions, 5 answers each).
// Cadran items are placeholder competency labels.
// ============================================================================

import {
  TrimaRankingPayload,
  TrimaCadranPayload,
  TrimaCadranPostResponse,
  TrimaCadranNumber,
} from 'pages/questionnaire-trima/types/trima.types';

// ----------------------------------------------------------------------------
// Phase 1 mock — 24 questions, 5 answers each
// (Reduced labels here; in real BE will be richer)
// ----------------------------------------------------------------------------

const buildAnswer = (id: number, fr: string, rank: number) => ({
  id,
  label: { fr, en: fr }, // mock: same label both langs
  initialRank: rank,
});

export const MOCK_RANKING_PAYLOAD: TrimaRankingPayload = {
  questionnaireId: 9001,
  questions: [
    {
      id: 1,
      order: 1,
      label: { fr: 'Au travail, je suis capable…', en: 'At work, I am able to…' },
      answers: [
        buildAnswer(101, 'De contrôler la progression vers les objectifs', 1),
        buildAnswer(102, 'De gérer le changement', 2),
        buildAnswer(103, 'De fonctionner en mode de simultanéité', 3),
        buildAnswer(104, 'De favoriser l\'appui mutuel', 4),
        buildAnswer(105, 'De faire preuve d\'esprit de décision', 5),
      ],
    },
    {
      id: 2,
      order: 2,
      label: { fr: 'J\'excelle dans des environnements demandant…', en: 'I excel in environments requiring…' },
      answers: [
        buildAnswer(201, 'D\'être questionneur', 1),
        buildAnswer(202, 'D\'anticiper le changement', 2),
        buildAnswer(203, 'D\'être orienté client', 3),
        buildAnswer(204, 'De faire preuve d\'affirmation de soi', 4),
        buildAnswer(205, 'De faire montre d\'auto-discipline', 5),
      ],
    },
    {
      id: 3,
      order: 3,
      label: { fr: 'Je possède comme attribut…', en: 'I have as an attribute…' },
      answers: [
        buildAnswer(301, 'Une ouverture d\'esprit', 1),
        buildAnswer(302, 'De la diplomatie', 2),
        buildAnswer(303, 'De la détermination', 3),
        buildAnswer(304, 'Un sens pratique', 4),
        buildAnswer(305, 'Une compréhension stratégique', 5),
      ],
    },
    // Questions 4..24: generated procedurally to keep this file readable.
    // In the real BE, each will have its own curated content.
    ...Array.from({ length: 21 }, (_, i) => {
      const qIdx = i + 4;
      const baseId = qIdx * 100;
      return {
        id: qIdx,
        order: qIdx,
        label: {
          fr: `Question ${qIdx} (mock) — Cet énoncé me décrit :`,
          en: `Question ${qIdx} (mock) — This statement describes me:`,
        },
        answers: [
          buildAnswer(baseId + 1, `Option A (Q${qIdx})`, 1),
          buildAnswer(baseId + 2, `Option B (Q${qIdx})`, 2),
          buildAnswer(baseId + 3, `Option C (Q${qIdx})`, 3),
          buildAnswer(baseId + 4, `Option D (Q${qIdx})`, 4),
          buildAnswer(baseId + 5, `Option E (Q${qIdx})`, 5),
        ],
      };
    }),
  ],
};

// ----------------------------------------------------------------------------
// Phase 2 mocks — cadrans
// ----------------------------------------------------------------------------

const COMPETENCY_POOL_FR = [
  'Leadership', 'Communication', 'Adaptabilité', 'Rigueur', 'Créativité',
  'Empathie', 'Stratégie', 'Initiative', 'Persévérance', 'Curiosité',
  'Diplomatie', 'Autonomie', 'Méthode', 'Vision', 'Écoute',
  'Analyse', 'Synthèse', 'Pragmatisme', 'Pédagogie', 'Négociation',
  'Délégation', 'Organisation', 'Anticipation', 'Discernement',
  'Influence', 'Médiation', 'Innovation', 'Esprit critique',
  'Sens du collectif', 'Esprit d\'équipe', 'Loyauté', 'Intégrité',
  'Fiabilité', 'Engagement', 'Réactivité', 'Détermination',
  'Bienveillance', 'Patience', 'Tolérance', 'Souplesse',
  'Polyvalence', 'Persuasion', 'Conviction', 'Cohérence',
  'Authenticité', 'Humilité', 'Audace', 'Optimisme',
];

const buildCadran = (
  cadranNumber: TrimaCadranNumber,
  minSelections: number,
  itemCount: number,
  offset = 0,
): TrimaCadranPayload => ({
  cadranNumber,
  minSelections,
  sessionCompetenceId: 0,
  items: COMPETENCY_POOL_FR.slice(offset, offset + itemCount).map((label, i) => ({
    id: cadranNumber * 1000 + offset + i,
    label: { fr: label, en: label }, // mock
  })),
});

export const MOCK_CADRAN_1: TrimaCadranPayload = buildCadran(1, 10, 48);
export const MOCK_CADRAN_2: TrimaCadranPayload = buildCadran(2, 5, 36, 4);
export const MOCK_CADRAN_3: TrimaCadranPayload = buildCadran(3, 10, 42);
export const MOCK_CADRAN_4: TrimaCadranPayload = buildCadran(4, 5, 30, 8);

/**
 * Mock decision logic for `postCadran`:
 * - If user selected exactly minSelections, advance to next cadran (or 'done' after cadran 4).
 * - If user selected more, "refine" by returning a shrunken list with the same cadranNumber.
 *
 * This simulates the BE/ML behavior described in the brief.
 */
export const mockCadranPostResponse = (
  cadranNumber: TrimaCadranNumber,
  selectedCount: number,
  minSelections: number,
): TrimaCadranPostResponse => {
  if (selectedCount > minSelections) {
    // Simulate refinement: same cadranNumber, smaller item pool
    return { status: 'done' };
  }
  // Exact count → advance
  switch (cadranNumber) {
    case 1: return { status: 'next', payload: MOCK_CADRAN_2 };
    case 2: return { status: 'next', payload: MOCK_CADRAN_3 };
    case 3: return { status: 'next', payload: MOCK_CADRAN_4 };
    case 4: return { status: 'done' };
  }
};
